import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { useAccount } from '../../contexts/Account';
import { submitAOBFStep1 } from '../../services/advisor_services';
import { Enviroments } from '../../services/utils/types';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import AdvisorImageUploader from '../../components/Advisor/Profile/components/advisorImageUploader';
import { uploadToS3 } from '../../services/Amplify/amplify-files-upload';
import BuildingIcon from '../../icons/add-a-photo.svg';
import AdvisoryCloudLogo from '../../components/AdvisoryCloudLogo';
import RatingCard from './RatingCard';

const useStyles = (isSM) => ({
  main: {
    with: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    padding: isSM ? '16px 8px' : '16px 120px 0',
    justifyContent: 'center',
    width: '100%',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '600px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: { xxs: '32px', xs: '24px' },
    letterSpacing: '-0.4300000071525574px',
    marginBottom: '16px',
    padding: { xxs: '0px 8px', xs: '0px' },
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    maxWidth: '360px',
    textAlign: 'center',
    padding: { xxs: '0px 8px', xs: '0px' },
  },
  info: {
    fontStyle: 'italic',
    fontSize: '14px',
    fontWeight: 300,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '16px',
  },
  gridContainerProps: {
    container: true,
    spacing: 0,
    justifyContent: 'center',
  },
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
  label: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '8px',
  },
  textField: {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        borderColor: '#C7D0DE',
      },
    },
  },
});

function AOBFStep1() {
  const { accountDetails } = useAccount();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = useStyles(isSM);
  const [firstName, setFirstName] = useState(accountDetails.givenName || '');
  const [lastName, setLastName] = useState(accountDetails.surName || '');
  const [motivation, setMotivation] = useState('');
  const [experience, setExperience] = useState('');
  const [avatarImage, setAvatarImage] = useState();
  const [originalImage, setOriginalImage] = useState();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();

  const motivationOptions = [
    'Elite Networking Connections',
    'Additional Income Potential',
    'Enhance My Professional Brand',
    'Reach My Long Term Professional Goals',
    'Work Less / Flexible Lifestyle',
  ];

  const experienceOptions = ['5+', '10+', '20+'];

  function setImageFiles(images) {
    setAvatarImage(images.image);
    setOriginalImage(images.originalImage);
  }

  function updateUsetiful() {
    try {
      window.USETIFUL.user.setTags({ primaryMotivation: motivation });
    } catch (error) {
      console.error(error);
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      let uploadedAvatarId = null;
      let uploadedOriginalId = null;
      let bucketImages = process.env.REACT_APP_S3_BUCKET_IMAGES;
      if (process.env.NODE_ENV === Enviroments.PRODUCTION) {
        bucketImages = process.env.REACT_APP_S3_BUCKET_IMAGES.replace(
          'us-west-2',
          'us-east-1'
        );
      }
      if (avatarImage) {
        const imageId = `${uuidv4()}.${avatarImage.type.split('/')[1]}`;
        await uploadToS3({
          BLOB_LOAD: avatarImage,
          CONTENT_TYPE: avatarImage.type,
          FILE_ID: imageId,
          S3_BUCKET: bucketImages,
        });
        uploadedAvatarId = `public/${imageId}`;
      }
      if (originalImage) {
        const imageId = `${uuidv4()}.${originalImage.type.split('/')[1]}`;
        await uploadToS3({
          BLOB_LOAD: originalImage,
          CONTENT_TYPE: originalImage.type,
          FILE_ID: imageId,
          S3_BUCKET: process.env.REACT_APP_S3_BUCKET_ORIGINAL_IMAGES,
        });
        uploadedOriginalId = `public/${imageId}`;
      }
      // Complete AOBF
      await submitAOBFStep1({
        id: accountDetails.id,
        givenName: firstName,
        surName: lastName,
        primaryMotivation: motivation,
        avatarImageId: uploadedAvatarId || '',
        originalImageId: uploadedOriginalId || '',
        experienceYears: experience,
      });
      updateUsetiful();
      history.push('/advisor/aobf/step2');
    } catch (error) {
      setIsLoading(false);
      setOpenSnackbar(true);
      console.error(error);
    }
  };

  const disableButton = () => {
    if (
      !firstName ||
      !lastName ||
      isLoading ||
      !motivation ||
      !experience ||
      !avatarImage
    ) {
      return true;
    }
    return false;
  };

  return (
    <>
      <Helmet>
        <title>Profile Builder - AdvisoryCloud</title>
      </Helmet>

      <Box sx={classes.main}>
        <Box sx={classes.header}>
          <AdvisoryCloudLogo
            removeHyperlink
            onClick={() => {}}
            width={isSM ? 240 : 180}
            height={isSM ? 80 : 60}
            cursor="default"
          />
        </Box>

        <Box sx={classes.body}>
          <Typography sx={classes.title}>
            Let's Set Up Your AdvisorCloud Profile
          </Typography>
          <Typography sx={{ ...classes.subtitle, maxWidth: '480px' }}>
            Tell us about yourself so we can better match you with senior-level
            opportunities like joining boards, advisory work, and high-level
            networking in a worth-your-time way.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <Typography sx={classes.label}>Your name:</Typography>
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid item xxs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="First"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  sx={{
                    ...classes.textField,
                    paddingRight: { xxs: 0, md: '6px' },
                  }}
                  id="aobf-first-name"
                />
              </Grid>
              <Grid item xxs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Last"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  sx={{
                    ...classes.textField,
                    paddingLeft: { xxs: 0, md: '6px' },
                  }}
                  id="aobf-last-name"
                />
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <Typography sx={classes.label}>
                  Primary Motivation as an Advisor:
                </Typography>
              </Grid>
              <Grid {...classes.gridItemProps}>
                <Select
                  fullWidth
                  value={motivation}
                  onChange={(e) => setMotivation(e.target.value)}
                  displayEmpty
                  sx={{ marginBottom: '8px', borderRadius: '10px' }}
                  id="aobf-motivation"
                >
                  <MenuItem value="" disabled>
                    Select one
                  </MenuItem>
                  {motivationOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid {...classes.gridItemProps}>
                <Typography sx={classes.label}>
                  How many years of experience do you have?
                </Typography>
              </Grid>
              <Grid {...classes.gridItemProps}>
                <Select
                  fullWidth
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  displayEmpty
                  sx={{ borderRadius: '10px', marginBottom: '8px' }}
                  id="aobf-motivation"
                >
                  <MenuItem value="" disabled>
                    Select one
                  </MenuItem>
                  {experienceOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  width: '100%',
                  marginTop: '5px',
                }}
              >
                <Box
                  sx={{
                    mx: 'auto',
                  }}
                  id="aobf-image-uploader"
                >
                  <AdvisorImageUploader
                    resultFiles={setImageFiles}
                    placeholder={`
                    <div 
                      style="
                        color: white;
                        background-color:#D9D9D9;
                        width: 120px;
                        height: 120px;
                      "
                    >
                      <img src="${BuildingIcon}" width="50" style="padding-top: 10px; margin-left: 4px;" />
                      <p style="margin: 0px; font-size: 13px; font-weight: 500;">Add a Profile Picture</p>
                    </div>`}
                  />
                </Box>
              </Box>
              <Grid {...classes.gridItemProps}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={disableButton()}
                  id="aobf-submit-button"
                  sx={{
                    borderRadius: '50px',
                    backgroundColor: '#3171F6',
                    '&:hover': { backgroundColor: '#3171F6' },
                    height: '48px',
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 700,
                    mb: '16px',
                    mt: '8px',
                  }}
                >
                  {isLoading ? <LoadingLogoSkeleton /> : 'Continue'}
                </Button>
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <RatingCard
                  quote="I applaud AdvisoryCloud's effort to disrupt an industry that's been dependent on 'word-of-mouth' and 'who-you-know.'"
                  name="Mary McDougall"
                  title="Fmr. Vice President, Astia"
                />
              </Grid>
            </Grid>
            <ACSnackbar
              onClose={() => setOpenSnackbar(false)}
              open={openSnackbar}
              severity="error"
              text="Something went wrong"
            />
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default AOBFStep1;
